/**
 * riskService — Portfolio Risk Analyzer API client.
 *
 * Follows the existing service pattern: caller passes the JWT token
 * (from useAuth()) so the service stays stateless.
 *
 * Backend routes (API GW):
 *   POST /v1/risk/analyze
 *   GET  /v1/risk/analyses?limit=N
 *   GET  /v1/risk/analyses/:id
 */

const API_BASE =
  process.env.REACT_APP_API_URL ||
  'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

// ─── Response types ───────────────────────────────────────────────────────────

export interface RiskSummary {
  overall_risk_level: string;
  headline: string;
  top_3_observations: string[];
}

export interface RiskRec {
  rec_id: string;
  severity: 'info' | 'watch' | 'act_soon' | 'act_now';
  confidence?: string;
  sources?: string[];
  symbol?: string;
  title: string;
  what?: string;
  why?: string[];
  analysis?: { observation?: string; consideration?: string };
  disclaimer?: string;
}

export interface AiConsensus {
  consensus: RiskRec[];
  claude_only: RiskRec[];
  chatgpt_only: RiskRec[];
}

export interface LlmStatus {
  claude: 'fulfilled' | 'rejected';
  chatgpt: 'fulfilled' | 'rejected';
}

export interface AccountAnalysis {
  accountId: string;
  snapshot: any;
  summary: RiskSummary;
  recs: RiskRec[];
  aiConsensus: AiConsensus;
  charts: {
    treemap: any;
    beforeAfter: any;
    radar: any;
    sourceBars: any;
  };
  llmStatus: LlmStatus;
}

export interface AnalysisResponse {
  analysisId: string;
  runAt: string;
  mode: 'manual' | 'scheduled';
  accountIds: string[];
  analyses: Record<string, AccountAnalysis>;
  meta: { wall_clock_ms: number };
}

export interface HistoryItem {
  analysisId: string;
  runAt: string;
  mode: string;
  overall_risk_level: string;
  headline: string;
  rec_count: number;
}

// ─── API calls ────────────────────────────────────────────────────────────────

function authHeaders(token: string) {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err: any = new Error(
      (body as any)?.error?.message || `HTTP ${res.status}`
    );
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return res.json() as Promise<T>;
}

export async function analyzeRisk(
  token: string,
  accountIds: string[]
): Promise<AnalysisResponse> {
  const res = await fetch(`${API_BASE}/v1/risk/analyze`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ accountIds }),
  });
  return handleResponse<AnalysisResponse>(res);
}

export async function listAnalyses(
  token: string,
  limit = 30
): Promise<{ analyses: HistoryItem[]; next_cursor: string | null }> {
  const res = await fetch(`${API_BASE}/v1/risk/analyses?limit=${limit}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function getAnalysis(
  token: string,
  id: string
): Promise<{ analysis: any }> {
  const res = await fetch(`${API_BASE}/v1/risk/analyses/${id}`, {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}
